import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = ['#0891b2', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#ca8a04', '#16a34a', '#0f766e', '#4f46e5', '#9333ea', '#e11d48', '#64748b'];

export const isInventoryChart = (chart) => (
  chart
  && ['bar', 'donut', 'line'].includes(chart.type)
  && typeof chart.title === 'string'
  && Array.isArray(chart.data)
  && chart.data.length >= 2
  && chart.data.length <= 12
  && chart.data.every((item) => typeof item?.label === 'string' && Number.isFinite(item?.value))
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900">
      <p className="font-medium text-foreground">{item.label}</p>
      <p className="mt-0.5 font-mono text-primary">{item.value.toLocaleString('es-AR')}</p>
    </div>
  );
};

export const InventoryChart = ({ chart }) => {
  if (!isInventoryChart(chart)) return null;

  const data = chart.data.map((item, index) => ({ ...item, color: COLORS[index % COLORS.length] }));
  const commonAxis = {
    tick: { fill: 'currentColor', fontSize: 10 },
    axisLine: false,
    tickLine: false,
  };

  return (
    <section className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-700" aria-label={`Gráfico: ${chart.title}`}>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{chart.title}</p>
      <div className="h-48 w-full" role="img" aria-label={chart.title}>
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'donut' ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} cornerRadius={3} stroke="transparent">
                {data.map((item) => <Cell key={item.label} fill={item.color} />)}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          ) : chart.type === 'line' ? (
            <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.12} />
              <XAxis dataKey="label" {...commonAxis} />
              <YAxis allowDecimals={false} {...commonAxis} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey="value" stroke="#0891b2" strokeWidth={2.5} dot={{ r: 3, fill: '#0891b2' }} activeDot={{ r: 5 }} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.12} />
              <XAxis dataKey="label" {...commonAxis} />
              <YAxis allowDecimals={false} {...commonAxis} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36}>
                {data.map((item) => <Cell key={item.label} fill={item.color} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        {data.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}: {item.value.toLocaleString('es-AR')}
          </span>
        ))}
      </div>
    </section>
  );
};
